// ============================================================================
// stockLedgerService — Client half of the stock ledger foundation (FI2.a2)
// ----------------------------------------------------------------------------
// Components never import firebase/* directly (CLAUDE.md data access rule).
// All stock ledger mutations flow through callable() or guarded client writes.
// ============================================================================

import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { callable } from '../callable'
import {
  STOCK_LOCATIONS_COLLECTION,
  STOCK_MOVEMENTS_COLLECTION,
  type StockLocation,
  type StockLocationKind,
  type StockMovement,
  type StockMovementType,
} from '../../types/stock'

export const KNOWN_STOCK_LEDGER_ERROR_CODES = [
  'STOCK_INSUFFICIENT',
  'ITEM_NOT_FOUND',
  'LOCATION_NOT_FOUND',
  'LOCATION_ARCHIVED',
  'LOT_NOT_FOUND',
  'FOREIGN_WORKSPACE',
  'INVALID_STEP',
  'INVALID_MOVEMENT',
  'UNKNOWN_TYPE',
  'REQUEST_ID_CONFLICT',
  'LOT_EXPIRY_MISMATCH',
  'SERIAL_EXISTS',
  'TRACKED_NEEDS_LOT',
  'SERIAL_NEEDS_SCAN',
  'ALREADY_RECEIVED',
  'ALREADY_CANCELLED',
] as const

export type KnownStockLedgerErrorCode = (typeof KNOWN_STOCK_LEDGER_ERROR_CODES)[number]

export class StockLedgerClientError extends Error {
  readonly code: string
  readonly functionsCode?: string
  readonly details?: unknown

  constructor(
    code: string,
    message: string,
    options?: { functionsCode?: string; details?: unknown; cause?: unknown },
  ) {
    super(message)
    this.name = 'StockLedgerClientError'
    this.code = code
    this.functionsCode = options?.functionsCode
    this.details = options?.details
    this.cause = options?.cause
    Object.setPrototypeOf(this, StockLedgerClientError.prototype)
  }
}

/**
 * Recursively removes keys whose value is undefined from an object or array.
 */
export function stripUndefined<T>(value: T): T {
  if (value === undefined) return undefined as unknown as T
  if (value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)) as unknown as T
  }
  const proto = Object.getPrototypeOf(value)
  if (proto !== Object.prototype && proto !== null) {
    return value
  }
  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    if (v !== undefined) {
      result[k] = stripUndefined(v)
    }
  }
  return result as T
}

export interface PostStockMovementInput {
  itemId: string
  type: StockMovementType
  quantity?: number
  delta?: number
  locationId?: string
  lotId?: string
  reason?: string
  note?: string
  source?: { kind: 'MANUAL' | 'SUGGESTION'; id: string }
  lotCode?: string
  expiryDate?: string | Date | unknown
  serials?: string[]
}

export interface PostStockMovementsResult {
  movementIds: string[]
  pendingApprovals?: Array<{ itemId: string; adjustmentId: string }>
}

export interface PostStockMovementsOptions {
  requestId?: string
}

const KNOWN_CODES_SET = new Set<string>(KNOWN_STOCK_LEDGER_ERROR_CODES)

const mapToStockLedgerError = (err: unknown): StockLedgerClientError => {
  if (err instanceof StockLedgerClientError) return err

  const errObj =
    typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : undefined
  const maybeCode =
    errObj && 'code' in errObj && typeof errObj.code === 'string' ? errObj.code : undefined
  const maybeMsg =
    errObj && 'message' in errObj && typeof errObj.message === 'string'
      ? errObj.message
      : 'Stock movement failed.'
  const details = errObj && 'details' in errObj ? errObj.details : undefined

  let code: string = maybeCode ?? 'unknown'
  if (KNOWN_CODES_SET.has(maybeMsg)) {
    code = maybeMsg
  } else if (typeof details === 'string' && KNOWN_CODES_SET.has(details)) {
    code = details
  } else if (typeof details === 'object' && details !== null) {
    const detailsObj = details as Record<string, unknown>
    if ('code' in detailsObj && typeof detailsObj.code === 'string') {
      code = detailsObj.code
    }
  } else if (maybeCode && maybeCode !== 'unknown') {
    code = maybeCode
  }

  return new StockLedgerClientError(code, maybeMsg, {
    functionsCode: maybeCode,
    details,
    cause: err,
  })
}

const generateRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`

/**
 * Posts stock movements through the server callable postStockMovements.
 * Automatically generates a UUID requestId for idempotent replay protection.
 */
export const postStockMovements = async (
  workspaceId: string,
  movements: PostStockMovementInput[],
  options?: PostStockMovementsOptions,
): Promise<PostStockMovementsResult> => {
  const requestId = options?.requestId ?? generateRequestId()

  try {
    return await callable<
      { workspaceId: string; movements: PostStockMovementInput[]; requestId: string },
      PostStockMovementsResult
    >('postStockMovements', stripUndefined({
      workspaceId,
      movements,
      requestId,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Sets location reorder points on a stock item through server callable setStockReorderPoints.
 */
export const setStockReorderPoints = async (
  workspaceId: string,
  itemId: string,
  patch: Record<string, number | null>,
): Promise<void> => {
  try {
    await callable<
      { workspaceId: string; itemId: string; patch: Record<string, number | null> },
      void
    >('setStockReorderPoints', stripUndefined({
      workspaceId,
      itemId,
      patch,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

export interface CreateStockItemEntryInput {
  name: string
  sku: string
  unit: string
  location?: string | null
  category?: string | null
  tags?: string[]
  costPerUnit?: number | null
  supplierId?: string | null
  minThreshold?: number | null
  maxThreshold?: number | null
  reorderPoint?: number | null
  reorderQuantity?: number | null
  imageUrl?: string | null
  barcode?: string | null
  expiryDate?: unknown
  serialNumbers?: string[]
  notes?: string
  openingQuantity?: number
  quantityStep?: 1 | 0.5 | 0.1 | 0.01
  tracking?: 'NONE' | 'BATCH' | 'SERIAL'
  locationId?: string
}

export interface UpdateStockItemDetailsOptions {
  targetQuantity?: number
  maintenance?: boolean
  requestId?: string
}

export interface ReceiveRestockRequestInput {
  requestId: string
  receivedQuantity: number
  locationId?: string
  workspaceId?: string
  lotCode?: string
  expiryDate?: unknown
  serials?: string[]
}

export interface ResolveStockAdjustmentInput {
  adjustmentId: string
  decision: 'APPROVE' | 'REJECT'
  note?: string
  workspaceId?: string
}

/**
 * Creates one or more stock items through the server callable createStockItems.
 */
export const createStockItems = async (
  workspaceId: string,
  items: CreateStockItemEntryInput[],
  options?: { requestId?: string },
): Promise<string[]> => {
  const requestId = options?.requestId ?? generateRequestId()
  try {
    return await callable<
      { workspaceId: string; items: CreateStockItemEntryInput[]; requestId: string },
      string[]
    >('createStockItems', stripUndefined({
      workspaceId,
      items,
      requestId,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Updates stock item details and optionally posts an ADJUST movement in one server tx.
 */
export const updateStockItemDetails = async (
  workspaceId: string,
  itemId: string,
  patch: Record<string, unknown>,
  options?: UpdateStockItemDetailsOptions,
): Promise<{ success: boolean; itemId: string; movementId?: string }> => {
  const requestId = options?.requestId ?? generateRequestId()
  try {
    return await callable<
      {
        workspaceId: string
        itemId: string
        patch: Record<string, unknown>
        targetQuantity?: number
        maintenance?: boolean
        requestId?: string
      },
      { success: boolean; itemId: string; movementId?: string }
    >('updateStockItemDetails', stripUndefined({
      workspaceId,
      itemId,
      patch,
      targetQuantity: options?.targetQuantity,
      maintenance: options?.maintenance,
      requestId,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Toggles maintenance mode on a stock item through server callable setStockMaintenance.
 */
export const setStockMaintenance = async (
  workspaceId: string,
  itemId: string,
  on: boolean,
): Promise<{ success: boolean; status: string; maintenance: boolean }> => {
  try {
    return await callable<
      { workspaceId: string; itemId: string; on: boolean },
      { success: boolean; status: string; maintenance: boolean }
    >('setStockMaintenance', stripUndefined({
      workspaceId,
      itemId,
      on,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Marks a restock request received and posts an IN movement via receiveRestockRequest.
 */
export const receiveRestockRequest = async (
  input: ReceiveRestockRequestInput,
): Promise<{ success: boolean; requestId: string }> => {
  try {
    return await callable<ReceiveRestockRequestInput, { success: boolean; requestId: string }>(
      'receiveRestockRequest',
      stripUndefined(input),
    )
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Resolves a pending stock adjustment (APPROVE / REJECT) via resolveStockAdjustment.
 */
export const resolveStockAdjustment = async (
  input: ResolveStockAdjustmentInput,
): Promise<{ success: boolean; adjustmentId: string; status: 'approved' | 'rejected' }> => {
  try {
    return await callable<
      ResolveStockAdjustmentInput,
      { success: boolean; adjustmentId: string; status: 'approved' | 'rejected' }
    >('resolveStockAdjustment', stripUndefined(input))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

const toStockLocation = (id: string, data: DocumentData): StockLocation => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  name: String(data.name ?? ''),
  kind: (data.kind as StockLocationKind) ?? 'STORE',
  isDefault: Boolean(data.isDefault),
  archived: Boolean(data.archived),
  createdAt: data.createdAt ?? null,
  createdById: data.createdById != null ? String(data.createdById) : null,
})

/**
 * Subscribes to active (non-archived) stock locations in a workspace, ordered by name ASC.
 */
export const subscribeStockLocations = (
  workspaceId: string,
  onData: (locations: StockLocation[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
): Unsubscribe => {
  const q = query(
    collection(db, STOCK_LOCATIONS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('archived', '==', false),
    orderBy('name', 'asc'),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const locations = snapshot.docs.map((docSnap) => toStockLocation(docSnap.id, docSnap.data()))
      onData(locations)
    },
    (error) => {
      onError?.(error)
    },
  )
}

/**
 * Fetches active (non-archived) stock locations in a workspace, ordered by name ASC.
 */
export const fetchStockLocations = async (
  workspaceId: string,
  db: Firestore = firestoreDb,
): Promise<StockLocation[]> => {
  if (!workspaceId) return []
  const q = query(
    collection(db, STOCK_LOCATIONS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('archived', '==', false),
    orderBy('name', 'asc'),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => toStockLocation(docSnap.id, docSnap.data()))
}

export interface CreateStockLocationInput {
  name: string
  kind: StockLocationKind
  createdById?: string | null
}

/**
 * Creates a new custom stock location via server callable createStockLocation.
 */
export const createStockLocation = async (
  workspaceId: string,
  input: CreateStockLocationInput,
  _actorId: string,
  _db: Firestore = firestoreDb,
): Promise<{ id: string }> => {
  void _actorId
  void _db
  const trimmed = input.name.trim()
  if (trimmed.length < 1 || trimmed.length > 80) {
    throw new StockLedgerClientError(
      'INVALID_NAME',
      'Location name must be between 1 and 80 characters.',
    )
  }
  try {
    return await callable<
      { workspaceId: string; name: string; kind: StockLocationKind },
      { id: string }
    >('createStockLocation', stripUndefined({
      workspaceId,
      name: trimmed,
      kind: input.kind,
    }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Renames an existing stock location via server callable renameStockLocation.
 */
export const renameStockLocation = async (
  locationId: string,
  name: string,
  _actorId: string,
  _db: Firestore = firestoreDb,
): Promise<void> => {
  void _actorId
  void _db
  const trimmed = name.trim()
  if (trimmed.length < 1 || trimmed.length > 80) {
    throw new StockLedgerClientError(
      'INVALID_NAME',
      'Location name must be between 1 and 80 characters.',
    )
  }
  try {
    await callable<{ locationId: string; name: string }, { success: boolean }>(
      'renameStockLocation',
      stripUndefined({
        locationId,
        name: trimmed,
      }),
    )
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

/**
 * Archives a stock location via server callable archiveStockLocation.
 */
export const archiveStockLocation = async (
  locationId: string,
  _actorId: string,
  _db: Firestore = firestoreDb,
): Promise<void> => {
  void _actorId
  void _db
  try {
    await callable<{ locationId: string }, { success: boolean }>(
      'archiveStockLocation',
      stripUndefined({
        locationId,
      }),
    )
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

export interface SendStockTransferInput {
  workspaceId: string
  fromLocationId: string
  toLocationId: string
  lines: Array<{
    itemId: string
    quantity: number
    lotId?: string | null
    serials?: string[] | null
  }>
  note?: string | null
  requestId?: string
}

export interface SendStockTransferResult {
  success: boolean
  transferId: string
}

export const sendStockTransfer = async (
  input: SendStockTransferInput,
): Promise<SendStockTransferResult> => {
  const requestId = input.requestId ?? generateRequestId()
  try {
    return await callable<
      SendStockTransferInput & { requestId: string },
      SendStockTransferResult
    >('sendStockTransfer', stripUndefined({ ...input, requestId }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

export const receiveStockTransfer = async (
  workspaceId: string,
  transferId: string,
  options?: { requestId?: string },
): Promise<{ success: boolean; transferId: string; movementIds: string[] }> => {
  const requestId = options?.requestId ?? generateRequestId()
  try {
    return await callable<
      { workspaceId: string; transferId: string; requestId: string },
      { success: boolean; transferId: string; movementIds: string[] }
    >('receiveStockTransfer', stripUndefined({ workspaceId, transferId, requestId }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

export const cancelStockTransfer = async (
  workspaceId: string,
  transferId: string,
  note?: string | null,
  options?: { requestId?: string },
): Promise<{ success: boolean; transferId: string }> => {
  const requestId = options?.requestId ?? generateRequestId()
  try {
    return await callable<
      { workspaceId: string; transferId: string; note?: string | null; requestId: string },
      { success: boolean; transferId: string }
    >('cancelStockTransfer', stripUndefined({ workspaceId, transferId, note, requestId }))
  } catch (error) {
    throw mapToStockLedgerError(error)
  }
}

const toStockMovement = (id: string, data: DocumentData): StockMovement => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  itemId: String(data.itemId ?? ''),
  type: data.type as StockMovementType,
  delta: Number(data.delta ?? 0),
  before: Number(data.before ?? 0),
  after: Number(data.after ?? 0),
  locationId: String(data.locationId ?? ''),
  lotId: data.lotId != null ? String(data.lotId) : null,
  reason: data.reason != null ? String(data.reason) : null,
  note: data.note != null ? String(data.note) : null,
  source: data.source ?? { kind: 'MANUAL', id: '' },
  actorId: String(data.actorId ?? ''),
  actorRole: String(data.actorRole ?? ''),
  approvedById: data.approvedById != null ? String(data.approvedById) : null,
  createdAt: data.createdAt ?? null,
  undoOf: data.undoOf != null ? String(data.undoOf) : null,
  undoneBy: data.undoneBy != null ? String(data.undoneBy) : null,
})

/**
 * Subscribes to the audit trail movements for a specific stock item, newest first, limit 50.
 */
export const subscribeItemMovements = (
  workspaceId: string,
  itemId: string,
  onData: (movements: StockMovement[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
): Unsubscribe => {
  const q = query(
    collection(db, STOCK_MOVEMENTS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('itemId', '==', itemId),
    orderBy('createdAt', 'desc'),
    limit(50),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const movements = snapshot.docs.map((docSnap) => toStockMovement(docSnap.id, docSnap.data()))
      onData(movements)
    },
    (error) => {
      onError?.(error)
    },
  )
}
